import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, ArrowRight, Mail, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function seatRate(seats, cycle) {
  if (cycle === 'annual') return seats >= 6 ? 250 : seats >= 2 ? 300 : 350;
  return seats >= 6 ? 25 : seats >= 2 ? 30 : 35;
}

function tierLabel(seats) {
  if (seats >= 6) return { label: 'Scale', accent: 'text-emerald-400', bg: 'bg-emerald-500/10' };
  if (seats >= 2) return { label: 'Team', accent: 'text-[#007AFF]', bg: 'bg-[#007AFF]/10' };
  return { label: 'Solo', accent: 'text-zinc-300', bg: 'bg-white/5' };
}

/**
 * Interactive seat slider. Computes the tier rate and total in real-time as
 * the user drags. Highlights the savings vs. flat $35/seat to anchor value.
 */
export function SeatCalculator({ theme = 'dark' }) {
  const [searchParams] = useSearchParams();
  const presetSeats = Math.min(Math.max(parseInt(searchParams.get('seats') || '0', 10), 1), 25) || 3;
  const presetCycle = searchParams.get('cycle') === 'annual' ? 'annual' : 'monthly';
  const [seats, setSeats] = useState(presetSeats);
  const [cycle, setCycle] = useState(presetCycle);
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDark = theme === 'dark';

  // Re-sync if the URL query params change while the page is mounted (rare)
  useEffect(() => {
    if (parseInt(searchParams.get('seats') || '0', 10)) setSeats(presetSeats);
    if (searchParams.get('cycle')) setCycle(presetCycle);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const rate = seatRate(seats, cycle);
  const total = seats * rate;
  const flatRate = cycle === 'annual' ? 350 : 35;
  const flatTotal = seats * flatRate;
  const savings = flatTotal - total;
  const { label, accent, bg } = useMemo(() => tierLabel(seats), [seats]);

  // "Email me this quote" inline flow
  const [emailQuoteOpen, setEmailQuoteOpen] = useState(false);
  const [quoteEmail, setQuoteEmail] = useState('');
  const [quoteSending, setQuoteSending] = useState(false);
  const [quoteSentTo, setQuoteSentTo] = useState('');
  const [quoteError, setQuoteError] = useState('');

  const handleSaveQuote = async (e) => {
    e.preventDefault();
    setQuoteError('');
    if (!quoteEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setQuoteError('Please enter a valid email address.');
      return;
    }
    setQuoteSending(true);
    try {
      const { data } = await axios.post(`${API}/marketing/save-quote`, {
        email: quoteEmail.trim(),
        seats,
        cycle,
      });
      setQuoteSentTo(data?.sent_to || quoteEmail.trim());
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setQuoteError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again.');
    } finally {
      setQuoteSending(false);
    }
  };

  const cardBg = isDark ? 'bg-[#141414] border-white/10' : 'bg-white border-slate-200';
  const labelColor = isDark ? 'text-zinc-400' : 'text-slate-500';
  const valueColor = isDark ? 'text-white' : 'text-slate-900';
  const subTone = isDark ? 'text-zinc-500' : 'text-slate-400';

  return (
    <div className={`max-w-2xl mx-auto border ${cardBg} rounded-2xl p-6 sm:p-8`} data-testid="seat-calculator">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Users className={`w-4 h-4 ${accent}`} />
          <p className={`text-xs font-bold uppercase tracking-[0.2em] ${labelColor}`}>Calculate your cost</p>
        </div>
        <div className={`inline-flex p-1 rounded-full border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
          {['monthly', 'annual'].map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setCycle(c)}
              className={`px-3 py-1 text-[10px] font-medium uppercase tracking-wider rounded-full transition-all ${
                cycle === c
                  ? (isDark ? 'bg-white/10 text-white' : 'bg-slate-900 text-white')
                  : (isDark ? 'text-zinc-500' : 'text-slate-500')
              }`}
              data-testid={`calc-cycle-${c}`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between mb-2">
        <p className={`text-xs ${subTone}`}>Team size</p>
        <span
          className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full ${bg} ${accent}`}
          data-testid="calc-tier-badge"
        >
          {label} tier
        </span>
      </div>

      <div className="flex items-baseline gap-2 mb-1">
        <span className={`text-5xl sm:text-6xl font-bold tracking-tighter ${valueColor}`} style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="calc-seat-count">
          {seats}
        </span>
        <span className={`text-sm ${subTone}`}>seat{seats === 1 ? '' : 's'}</span>
      </div>

      <input
        type="range"
        min="1"
        max="25"
        value={seats}
        onChange={(e) => setSeats(parseInt(e.target.value, 10))}
        className={`w-full mt-4 mb-6 h-2 rounded-full appearance-none cursor-pointer ${isDark ? 'bg-white/10' : 'bg-slate-200'} accent-[#007AFF]`}
        data-testid="calc-slider"
        aria-label="Number of seats"
      />

      <div className={`grid grid-cols-2 gap-4 pt-4 border-t ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
        <div>
          <p className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${labelColor} mb-1`}>Per seat</p>
          <p className={`text-2xl font-bold ${valueColor}`} style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="calc-per-seat">
            ${rate}<span className={`text-xs font-normal ${subTone}`}>/{cycle === 'annual' ? 'yr' : 'mo'}</span>
          </p>
        </div>
        <div className="text-right">
          <p className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${labelColor} mb-1`}>Your total</p>
          <p className={`text-2xl font-bold ${accent}`} style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="calc-total">
            ${total.toLocaleString()}<span className={`text-xs font-normal ${subTone}`}>/{cycle === 'annual' ? 'yr' : 'mo'}</span>
          </p>
        </div>
      </div>

      {savings > 0 && (
        <p className={`text-center text-xs mt-4 ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`} data-testid="calc-savings">
          You save <span className="font-semibold">${savings.toLocaleString()}</span> vs. flat ${flatRate}/seat
        </p>
      )}

      <Button
        type="button"
        onClick={() => {
          try { localStorage.setItem('seatPreset', JSON.stringify({ seats, cycle })); } catch { /* ignore quota */ }
          navigate(user ? `/billing?seats=${seats}&cycle=${cycle}` : '/register');
        }}
        className={`w-full mt-5 rounded-full h-11 text-sm font-semibold ${
          isDark
            ? 'bg-[#007AFF] hover:bg-[#005bb5] text-white transition-all hover:shadow-[0_0_30px_rgba(0,122,255,0.3)]'
            : 'bg-[#0047AB] hover:bg-[#003580] text-white'
        }`}
        data-testid="calc-checkout-cta"
      >
        Continue with {seats} seat{seats === 1 ? '' : 's'} <ArrowRight className="ml-1.5 w-4 h-4" />
      </Button>

      {/* Email me this quote — soft-conversion CTA for non-ready visitors */}
      {!user && (
        <div className="mt-4">
          {quoteSentTo ? (
            <div
              className={`flex items-center justify-center gap-2 text-xs ${isDark ? 'text-emerald-400' : 'text-emerald-700'}`}
              data-testid="calc-quote-sent"
            >
              <Check className="w-4 h-4" />
              <span>Quote sent to <span className="font-semibold">{quoteSentTo}</span>. Check your inbox.</span>
            </div>
          ) : !emailQuoteOpen ? (
            <button
              type="button"
              onClick={() => setEmailQuoteOpen(true)}
              className={`w-full flex items-center justify-center gap-1.5 text-xs ${isDark ? 'text-zinc-400 hover:text-zinc-200' : 'text-slate-500 hover:text-slate-800'} transition-colors`}
              data-testid="calc-email-quote-toggle"
            >
              <Mail className="w-3.5 h-3.5" />
              Email me this quote &mdash; finish later
            </button>
          ) : (
            <form onSubmit={handleSaveQuote} className="flex flex-col gap-2" data-testid="calc-email-quote-form">
              <div className="flex gap-2">
                <input
                  type="email"
                  value={quoteEmail}
                  onChange={(e) => setQuoteEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoFocus
                  disabled={quoteSending}
                  className={`flex-1 px-3 h-10 rounded-full text-sm outline-none border ${
                    isDark
                      ? 'bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-[#007AFF]'
                      : 'bg-white border-slate-200 text-slate-900 placeholder:text-slate-400 focus:border-[#0047AB]'
                  }`}
                  data-testid="calc-email-quote-input"
                />
                <Button
                  type="submit"
                  disabled={quoteSending}
                  className={`rounded-full h-10 px-4 text-xs font-semibold ${
                    isDark
                      ? 'bg-white/10 hover:bg-white/15 text-white'
                      : 'bg-slate-900 hover:bg-slate-700 text-white'
                  }`}
                  data-testid="calc-email-quote-submit"
                >
                  {quoteSending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Send'}
                </Button>
              </div>
              {quoteError && (
                <p className={`text-[11px] ${isDark ? 'text-red-400' : 'text-red-600'}`} data-testid="calc-email-quote-error">{quoteError}</p>
              )}
              <p className={`text-[10px] ${subTone}`}>One-click resume link, valid 14 days. We never share your email.</p>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
